import { VERSES } from '../data/verses.js';
import { QUESTIONS } from '../data/questions.js';
import { CHAPTERS, STORY_TOTAL } from '../data/story.js';

/** Scenes across the whole story, counted rather than typed in. */
const STORY_SCENES = CHAPTERS.reduce((total, chapter) => total + (chapter.scenes?.length ?? 0), 0);

/**
 * Free vs premium gating.
 *
 * These are the numbers; who they apply to is decided elsewhere. `isPremium`
 * comes from `useAccount`, which gets it from `/api/auth/me` and nowhere else —
 * there is no premium flag in local state to gate on, deliberately.
 *
 * Payment isn't wired up. When it is, the provider's webhook sets the flag on
 * the account row and every gate here starts passing; nothing in the UI
 * changes, since they all read from this file.
 */

export const FREE_LIMITS = {
  flashcards: 10,
  quizQuestions: 5,
  chatMessagesPerDay: 5,
  bookPaths: 2, // beyond this many books, paths are preview-only
  storyChapters: 4, // the whole of Act I, so the free tier is a real story
};

/**
 * Everything premium actually unlocks — and nothing it does not.
 *
 * The counts are read from the content itself rather than typed in, because
 * typed-in ones go stale silently and start lying. This list said "40+
 * questions" while the bank held 116, and "90+ verses" while it held 96.
 *
 * One entry is gone rather than restated: "Unlimited bookmarks". Bookmarking
 * has never been limited — toggleBookmark has no tier check at all — so it was
 * advertising as a paid feature something every free user already had. A
 * paywall that lists a benefit you cannot actually withhold is the kind of
 * thing a customer notices immediately after paying.
 *
 * Each line names the free limit it lifts, because "all 116 questions" only
 * means something next to the five a day it replaces.
 */
export const PREMIUM_FEATURES = [
  {
    icon: 'scroll',
    title: 'The whole story',
    short: `All ${STORY_TOTAL} chapters, illustrated. Free stops after ${FREE_LIMITS.storyChapters}.`,
    body: `All ${STORY_TOTAL} chapters and ${STORY_SCENES} scenes with painted illustrations, from creation to the new creation. Free stops after ${FREE_LIMITS.storyChapters}.`,
  },
  {
    icon: 'book',
    title: 'Every verse',
    short: `All ${VERSES.length} verses, not the first ${FREE_LIMITS.flashcards}.`,
    body: `All ${VERSES.length} verses across both testaments, instead of the first ${FREE_LIMITS.flashcards}.`,
  },
  {
    icon: 'flame',
    title: 'The full question bank',
    short: `All ${QUESTIONS.length} questions, not ${FREE_LIMITS.quizQuestions} a day.`,
    body: `All ${QUESTIONS.length} questions, each with an explanation of why the answer is what it is — instead of ${FREE_LIMITS.quizQuestions} a day.`,
  },
  {
    icon: 'target',
    title: 'Every practice mode at full depth',
    short: 'Quick quiz, Beat the clock, Weak spots and the Daily, all on the full bank.',
    body: 'Quick quiz, Beat the clock, Weak spots and the Daily all draw on the whole bank rather than the same handful.',
  },
  {
    icon: 'arch',
    title: 'All learning paths',
    short: `Any book end to end, not ${FREE_LIMITS.bookPaths}.`,
    body: `Work through any book end to end — Genesis, Psalms, John, Romans and the rest — rather than ${FREE_LIMITS.bookPaths}.`,
  },
  {
    icon: 'dove',
    title: 'Unlimited Scripture AI',
    short: `Ask without limit, not ${FREE_LIMITS.chatMessagesPerDay} messages a day.`,
    body: `Ask as much as you like, with the verse you are reading as context, instead of ${FREE_LIMITS.chatMessagesPerDay} messages a day.`,
  },
];

/** Slice a deck to what the current tier may see. */
export function limitDeck(deck, isPremium, limit = FREE_LIMITS.flashcards) {
  return isPremium ? deck : deck.slice(0, limit);
}

/** How many items are withheld from a free user. */
export function lockedCount(deck, isPremium, limit = FREE_LIMITS.flashcards) {
  return isPremium ? 0 : Math.max(0, deck.length - limit);
}

export function isPathLocked(pathIndex, isPremium) {
  return !isPremium && pathIndex >= FREE_LIMITS.bookPaths;
}
